import { InputProps } from '@chakra-ui/react';
import { Maybe } from '@monetize/types/common';
import { FunctionComponent as FC, useState } from 'react';
import {
  describeInvoiceDelay,
  getInvoiceDelayDirection,
  INVOICE_DELAY_MAX_DAYS,
  InvoiceDelayDirection,
  toInvoiceDelayValue,
} from '../../utils/invoiceDelay';
import { MBox, MFlex } from './chakra';
import { MCustomNumberInput } from './MCustomNumberInput';
import { MCustomSelect } from './MCustomSelect/MCustomSelect';
import MText from './MText';

type DirectionItem = { title: string; value: InvoiceDelayDirection };

/**
 * The title carries the unit so the row reads as a sentence next to the day count
 * ("7 days earlier"). Both sets live at module scope to keep the `items` reference
 * stable across renders.
 */
const DIRECTION_ITEMS: DirectionItem[] = [
  { title: 'Keep as scheduled', value: 'AS_SCHEDULED' },
  { title: 'days earlier', value: 'EARLIER' },
  { title: 'days after', value: 'AFTER' },
];

const DIRECTION_ITEMS_SINGULAR: DirectionItem[] = [
  { title: 'Keep as scheduled', value: 'AS_SCHEDULED' },
  { title: 'day earlier', value: 'EARLIER' },
  { title: 'day after', value: 'AFTER' },
];

export interface InvoiceDelayInputProps {
  value: Maybe<number>;
  onChange: (invoiceDelay: number) => void;
  onBlur?: () => void;
  name?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  variant?: InputProps['variant'];
}

/**
 * Signed `invoiceDelay` entry split into an unsigned day count plus an explicit
 * direction, so "7 days earlier" can never be saved as "7 days after".
 * The day input is hidden while the offset is 0 — that state reads as
 * "Keep as scheduled" on its own.
 */
export const InvoiceDelayInput: FC<InvoiceDelayInputProps> = ({
  value,
  onChange,
  onBlur,
  name = 'invoiceDelay',
  isDisabled,
  isReadOnly,
  variant,
}: InvoiceDelayInputProps) => {
  /**
   * Lets the field hold a transient empty string while the user retypes without
   * pushing an invalid value onto the form. Cleared on blur so the input snaps
   * back to whatever was last committed.
   */
  const [daysOverride, setDaysOverride] = useState<Maybe<string>>(null);

  const direction = getInvoiceDelayDirection(value);
  const days = Math.abs(value || 0);
  const daysText = daysOverride ?? (days ? String(days) : '');
  const showDays = direction !== 'AS_SCHEDULED';
  const directionItems =
    days === 1 ? DIRECTION_ITEMS_SINGULAR : DIRECTION_ITEMS;

  const handleDirectionChange = (newDirection: InvoiceDelayDirection) => {
    setDaysOverride(null);
    onChange(toInvoiceDelayValue(days || 1, newDirection));
  };

  const handleDaysChange = (valueAsString: string, valueAsNumber: number) => {
    // An empty or zero field is mid-edit, not a real value — hold the raw text
    // and wait for a valid day count.
    if (valueAsString === '' || !valueAsNumber) {
      setDaysOverride(valueAsString);
      return;
    }
    // Drop the override so the field renders the value actually committed. An
    // out-of-range entry ("45") is clamped on the way out, and the input has to
    // show that clamped count rather than the raw string the user typed.
    setDaysOverride(null);
    onChange(toInvoiceDelayValue(valueAsNumber, direction));
  };

  const handleBlur = () => {
    setDaysOverride(null);
    onBlur?.();
  };

  return (
    <MBox>
      <MFlex gap={2}>
        {showDays && (
          <MCustomNumberInput
            maxW="4.5rem"
            flexShrink={0}
            min={1}
            max={INVOICE_DELAY_MAX_DAYS}
            value={daysText}
            onChange={handleDaysChange}
            onBlur={handleBlur}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            variant={variant}
            styleProps={{
              'aria-label': 'Offset days',
              'data-testid': 'invoice-delay-days-input',
            }}
          />
        )}
        {/* The select sizes to its content inside a flex row — grow it so the
            field still spans its column like every other input on the page. */}
        <MBox flex="1" minW={0}>
          <MCustomSelect
            name={name}
            items={directionItems}
            value={direction}
            onChange={handleDirectionChange}
            onBlur={handleBlur}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isLazy
            inputProps={{
              variant,
              readOnly: isReadOnly,
              disabled: isDisabled,
              'aria-label': 'Offset direction',
            }}
          />
        </MBox>
      </MFlex>
      {showDays && (
        <MText fontSize="xs" color="fg.muted" mt={1}>
          {describeInvoiceDelay(value)}
        </MText>
      )}
    </MBox>
  );
};
